import { useLazyQuery } from '@apollo/client';
import { AppDataFormatEnum, Feature, Project, AppFeature } from '@bechtel/types';
import { get, find } from 'lodash';
import React, { useEffect } from 'react';
import { useAppUser } from '~/components/molecules/appuser/appuser-context';
import { GET_PROJECT_DATA_BY_ID, GET_PROJECT_CONFIG_FEATURES } from '@bechtel/client-gql';

export const ConfigContext = React.createContext(null);

export function ConfigProvider({ children, ...props }) {
  const { user } = useAppUser();
  let projectDateFormat = 'dd MMM yy';
  let projectTimeFormat = 'HH:mm aa';
  const [getProject, { called, data }] = useLazyQuery<{ projectsByIds: Project[] }>(GET_PROJECT_DATA_BY_ID, {
    notifyOnNetworkStatusChange: true,
  });
  const [getProjectFeatures, { data: projectFeaturesData }] = useLazyQuery<{ projectConfigById: Project[] }>(
    GET_PROJECT_CONFIG_FEATURES,
    {
      notifyOnNetworkStatusChange: true,
    }
  );
  const projectDetailArr: Project[] = get(data, 'projectsByIds', []);
  const projectDetail = projectDetailArr.length > 0 ? projectDetailArr[0] : null;
  const projectFeatures: Feature[] = get(projectFeaturesData, 'projectConfigById.features', []);

  const getFeature = (feature: AppFeature) => {
    return find(projectFeatures, (projFeature: Feature) => projFeature?.featureDefinition?.id == feature);
  };

  if (
    projectDetail &&
    projectDetail.projectConfiguration.appDataFormats &&
    projectDetail.projectConfiguration.appDataFormats.length > 0
  ) {
    const projectFormatData = projectDetail.projectConfiguration.appDataFormats.find(
      (inner) => inner.formatTypeId == AppDataFormatEnum.DATE_FORMAT
    );
    const projectDataTimeFormat = projectDetail.projectConfiguration.appDataFormats.find(
      (inner) => inner.formatTypeId == AppDataFormatEnum.TIME_FORMAT
    );
    if (projectFormatData && projectFormatData?.format) projectDateFormat = projectFormatData?.format;
    if (projectDataTimeFormat && projectDataTimeFormat?.format) projectTimeFormat = projectDataTimeFormat?.format;
  }

  useEffect(() => {
    if (!user || !user.project || called) return;
    getProject({
      variables: {
        ids: [user?.project?.id],
      },
    });
    getProjectFeatures({
      variables: {
        id: user?.project?.id,
      },
    });
  }, [user]);

  const value = {
    test: 'foo',
    timesheet: { weekStartOn: 1, weekDayCount: 7 },
    dateFormat: projectDateFormat,
    timeFormat: projectTimeFormat,
    blockAgeDateFormat: 'dd-MMM',
    unApproveWeldDays: 7,
    projectId: '3071629f-356e-428a-8ce2-f55938bb8055',
    weekStartsOn: 1,
    showInLinearFeet: false,
    features: {
      showMapView: getFeature(AppFeature.MAP_VIEW)?.enabled,
      showExpiringRodRequests: getFeature(AppFeature.EXPIRING_ROD_REQUEST)?.enabled,
      showShiftLog: getFeature(AppFeature.SHIFT_LOG)?.enabled,
      canApproveFitupInspection: getFeature(AppFeature.APPROVE_FIT_UP_INSPECTION)?.enabled,
      priorityWelds: {
        enabled: getFeature(AppFeature.PRIORITY_WELD)?.enabled,
        total: getFeature(AppFeature.PRIORITY_WELD)?.metaData?.properties?.limit?.value,
      },
    },
    markedPriorityWelds: 5,
    numPlanningNextWeeks: getFeature(AppFeature.PLANNING_WEEK)?.enabled
      ? getFeature(AppFeature.PLANNING_WEEK)?.metaData?.properties?.limit?.value
        ? Number(getFeature(AppFeature.PLANNING_WEEK)?.metaData?.properties?.limit?.value) - 1
        : 2
      : 2,
  };

  return (
    <ConfigContext.Provider value={value} {...props}>
      {children}
    </ConfigContext.Provider>
  );
}
